import {
    addDoc,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    setDoc,
    where,
    Timestamp
} from 'firebase/firestore';
import { axiosPrivate } from '@api/ApiHelper/axiosPrivate';
import {
    AppNotification,
    NOTIFICATIONS_QUERY_LIMIT,
    NOTIFICATIONS_RETENTION_DAYS,
    getReadUpToMillis,
    notificationReadsRef,
    notificationsRef
} from '@api/Notifications/Notification';

/**
 * Ventana de consulta de la campana. Se calcula al vuelo (no en un módulo) para
 * que una pestaña abierta durante días no siga mirando la ventana de cuando se
 * cargó.
 */
const desdeLaVentana = (): Timestamp =>
    Timestamp.fromMillis(Date.now() - NOTIFICATIONS_RETENTION_DAYS * 24 * 60 * 60 * 1000);

/**
 * ⚠️ **La query es de UN solo campo, a propósito.** `where('createdAt','>=')` +
 * `orderBy('createdAt')` + `limit` se resuelven con el índice simple que
 * Firestore mantiene solo: no exige índice compuesto, que es la regla del repo.
 * **El filtro de audiencia va en el cliente** — Firestore no sabe hacer OR entre
 * campos distintos, y meterlo en la query obligaría a un índice por cada
 * combinación de la UI.
 *
 * Y está acotada por partida doble (ventana + `limit`) porque **cada documento
 * leído se paga**, y esto vive en la barra superior de todas las pantallas.
 */
const consultaReciente = () =>
    query(
        notificationsRef,
        where('createdAt', '>=', desdeLaVentana()),
        orderBy('createdAt', 'desc'),
        limit(NOTIFICATIONS_QUERY_LIMIT)
    );

const mapear = (snapshot: any): AppNotification[] =>
    snapshot.docs.map((document: any) => ({ ...(document.data() as AppNotification), id: document.id }));

export default {
    async getRecentNotifications(): Promise<AppNotification[]> {
        const snapshot = await getDocs(consultaReciente());
        return mapear(snapshot);
    },

    /**
     * Suscripción en vivo para la campana. Devuelve la función para cortarla.
     *
     * ⚠️ `onSnapshot` **cobra por documento en cada entrega**, no solo al abrir
     * (regla de costo del CLAUDE.md raíz). Por eso la consulta va acotada por
     * ventana y por `limit`, y solo debe montarse UNA vez — vive en la barra,
     * que está en todas las pantallas.
     */
    subscribeToNotifications(
        onChange: (notifications: AppNotification[]) => void,
        onError?: (error: unknown) => void
    ): () => void {
        return onSnapshot(
            consultaReciente(),
            (snapshot) => onChange(mapear(snapshot)),
            (error) => {
                // Sin conexión o sin permisos: la campana se queda como está en
                // vez de vaciarse. Un aviso que desaparece es peor que uno viejo.
                console.error('No se pudieron escuchar las notificaciones:', error);
                onError?.(error);
            }
        );
    },

    /**
     * Marca leído TODO lo anterior a `upTo` con **UNA escritura**.
     *
     * ⚠️ Antes esto era un `updateDoc` con `arrayUnion` **por aviso** (hasta 50),
     * y cada uno se re-entregaba al `onSnapshot` de todas las campanas abiertas
     * de las nueve sucursales: ~1,250 lecturas ajenas por apertura. Ahora se
     * escribe un solo documento en `notification-reads`, que nadie escucha.
     *
     * El `readBy` de los avisos viejos NO se toca ni se migra: `isNotificationRead`
     * sigue respetándolo, así que lo ya marcado se queda marcado.
     */
    async markAllAsRead(uid: string, upTo: Date = new Date()): Promise<void> {
        await setDoc(doc(notificationReadsRef, uid), { readUpTo: upTo, uid, lastUpdate: new Date() }, { merge: true });
    },

    /** Hasta dónde leyó este usuario: **1 lectura**. */
    async getReadUpTo(uid: string): Promise<Date | undefined> {
        const snapshot = await getDoc(doc(notificationReadsRef, uid));
        if (!snapshot.exists()) return undefined;
        const value = snapshot.data()?.readUpTo;
        const millis = getReadUpToMillis(value);
        return millis ? new Date(millis) : undefined;
    },

    /**
     * Crea un aviso. Con `referenceId` es **idempotente**: si ya existe uno con
     * esa llave dentro de la ventana, no crea otro y devuelve el que había.
     *
     * Mismo patrón que los puntos y el ledger de inventario. Sin esto, un aviso
     * que se evalúa cada minuto ("el pedido 42 lleva 15 min") se duplicaría en
     * cada vuelta.
     */
    async createNotification(
        notification: Omit<AppNotification, 'id' | 'createdAt'>
    ): Promise<{ notification: AppNotification; isNew: boolean }> {
        if (notification.referenceId) {
            const existentes = await getDocs(
                query(notificationsRef, where('referenceId', '==', notification.referenceId), limit(1))
            );
            if (!existentes.empty) {
                const found = existentes.docs[0];
                // ⚠️ Devuelve `isNew: false` y NO solo el documento: quien llama
                // necesita saber que ya existía para no reenviar el push. Si esto
                // devolviera el aviso a secas, un evento que se evalúa cada
                // minuto notificaría en cada vuelta aunque no se duplicara el doc.
                return { notification: { ...(found.data() as AppNotification), id: found.id }, isNew: false };
            }
        }
        const payload: AppNotification = { ...notification, createdAt: new Date(), readBy: [] };
        const created = await addDoc(notificationsRef, payload);
        return { notification: { ...payload, id: created.id }, isNew: true };
    },

    /**
     * Crea el aviso **y dispara el push**. Es lo que deben usar los emisores.
     *
     * ⚠️ **Fire-and-forget y nunca lanza**, igual que `refreshPass` del Wallet y
     * el commit de inventario: estos avisos cuelgan de flujos que no pueden
     * romperse por ellos —crear una orden, cobrar—, y el precedente existe (el
     * `throw` del ticket de WhatsApp se llevó por delante el commit de
     * inventario del 35% de las ventas, 2026-08-19).
     *
     * El push va DESPUÉS de guardar y sin `await` en el camino crítico: el aviso
     * ya está en la campana, que llegue a la pantalla es un extra.
     */
    async notify(notification: Omit<AppNotification, 'id' | 'createdAt'>): Promise<void> {
        try {
            const { notification: created, isNew } = await this.createNotification(notification);
            // Si ya existía, ni se duplica el documento ni se reenvía el push.
            if (!isNew || !created.id) return;
            axiosPrivate
                .post('/push/send', {
                    title: notification.title,
                    body: notification.body,
                    link: notification.link,
                    // Agrupa por aviso: un reenvío reemplaza en vez de apilarse.
                    tag: notification.referenceId || created.id,
                    audience: notification.audience
                })
                .catch((error) => console.error('No se pudo entregar el push:', error));
        } catch (error) {
            console.error('No se pudo crear la notificación:', error);
        }
    },

    /**
     * Borra las notificaciones que ya salieron de la ventana de retención.
     *
     * ⚠️ **Existe desde el primer día a propósito.** `activity-logs` lleva 177 mil
     * documentos por no haber tenido esto; una colección que solo crece termina
     * costando dinero para siempre. Pensada para correrse a mano o desde un
     * script, no en cada carga de pantalla.
     */
    async purgeExpired(): Promise<number> {
        const viejas = await getDocs(query(notificationsRef, where('createdAt', '<', desdeLaVentana())));
        await Promise.all(viejas.docs.map((document) => deleteDoc(doc(notificationsRef, document.id))));
        return viejas.size;
    }
};
